import React, { useState, useEffect, useReducer, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import { Grid, CircularProgress, Backdrop } from "@mui/material";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { MessageDisplay } from "../../common/messageDisplay";
import { MuiComboBox } from "../../uiControls/muiComboBox";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import "../../uiControls/commonStyles.css";
import * as XLSX from "xlsx";
import Papa from "papaparse";

const initialDataImportSelectedValues = {
  dataSourceKey: 0,
  fileTypeKey: 0,
  rowToStartWith: 0,
  isValidFile: false,
};

export const DataImport = () => {
  const facilityId = useSelector((state) => state.SelectedValues.facilityId);
  const messageDispatch = useDispatch();
  const [dataImportSource, setDataImportSource] = useState([]);
  const [dataSourceTypes, setDataSourceTypes] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [fileTypeDetails, setFileTypeDetails] = useState([]);
  const [fileObj, setFileObj] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  const [dataImportSelectedValues, setDataImportSelectedValues] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialDataImportSelectedValues
  );

  const { isLoading: isDataSourceLoaded, sendRequest: fetchDataSources } = useHttpRequests();
  const { sendRequest: fetchFileTypeDetails } = useHttpRequests();
  const { isLoading: isImportProcessed, sendRequest: sendFileToImport } = useHttpRequests();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const buttonStyle = {
    backgroundColor: '#3a8ddd',
    color: 'white',
    cursor: 'pointer'
  };

  const disabledButtonStyle = {
    backgroundColor: '#dddddd',
    color: '#666666',
    cursor: 'not-allowed'
  };

  const resetValues = () => {
    updateDataImportSelectedValues("isValidFile", false);
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setIsVisible(false);
    setFileObj({});
  };

  useEffect(() => {
    setIsDisabled(!(dataImportSelectedValues.dataSourceKey > 0 && dataImportSelectedValues.fileTypeKey > 0));
  }, [dataImportSelectedValues]);

  useEffect(() => {
    setDataSourceTypes([]);
    setDataImportSource([]);
    resetValues();
    setFileTypes([]);
    getDataSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const getDataSources = () => {
    const getDataSourcesResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        console.log("Data sources fetch response returns error");
        return;
      }
      setDataImportSource(obj["datasources"]);
      setDataSourceTypes(
        obj["datasources"]?.length > 0
          ? obj["datasources"]?.map((t) => {
              return { id: t["datasourceKey"], label: t["name"] };
            })
          : []
      );
    };

    if (facilityId > 0) {
      updateDataImportSelectedValues(initialDataImportSelectedValues);
      fetchDataSources(
        {
          url: "DataSources/Import/" + facilityId,
          method: "GET",
          header: true,
        },
        getDataSourcesResponse
      );
    }
  };

  const handleFileUpload = (event) => {
    updateDataImportSelectedValues("isValidFile", false);
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setIsUploading(true);
    setIsVisible(false);
    const file = event.target.files[0];

    if (file) {
      setFileObj(file);
      const reader = new FileReader();

      if (file.name.endsWith(".csv")) {
        setIsVisible(true);
        reader.onload = (e) => {
          const contents = e.target.result;
          handleCSVData(contents);
        };
        reader.readAsText(file);
      } else if (file.name.endsWith(".xlsx")) {
        reader.onload = (e) => {
          const contents = e.target.result;
          handleXLSXData(contents);
        };
        reader.readAsArrayBuffer(file);
      } else if (file.name.endsWith(".xls")) {
        setIsUploading(false);
        messageDispatch(
          setMessageHandler({
            type: "validationError",
            message: ".xls is older version of excel, Use .xlsx file/newer version for excel.",
          })
        );
      }  
      else {
        setIsUploading(false);
        messageDispatch(
          setMessageHandler({
            type: "validationError",
            message: "Unsupported/invalid file format. Please upload valid CSV or XLSX file and try again."
          })
        );
      }

      fileInputRef.current.value = "";
    }
  };

  const handleCSVData = (data) => {
    Papa.parse(data, {
      header: false,
      dynamicTyping: true,
      skipEmptyLines: false,
      complete: (results) => {
        const dataWithoutHeaders = results.data.slice(
          dataImportSelectedValues.rowToStartWith - 1
        );

        const rawHeaders = dataWithoutHeaders[0];
        const headers = rawHeaders?.filter(
          (header) => header !== null && header !== ""
        );

        validateDataImport(headers?.map(toUpper));
        setIsUploading(false);
      },
      error: (error) => {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message: error.message,
          })
        );
      },
    });
  };

  const handleXLSXData = (xlsxData) => {
    const workBook = XLSX.read(xlsxData, { type: "binary" });
    const sheetName = workBook.SheetNames[0];
    const sheet = workBook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const headerColumns = [];

    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: col });
      const cell = sheet[cellAddress];
      headerColumns.push(cell ? cell.v : `Column${col}`);
    }

    validateDataImport(headerColumns?.map(toUpper));
    setIsUploading(false);
  };

  const toUpper = (x) => {
    return x?.toString().toUpperCase();
  };

  const removeSpecialCharacter = (inputValue) => {
    if (!inputValue) {
      return inputValue;
    }

    return inputValue
      .replace("&gt;", ">")
      .replace("&lt;", "<")
      .replace("&nbsp; &amp;", " &")
      .replace("&nbsp; ", " ")
      .replace("&nbsp;", "")
      .replace("&amp;", "&");
  };

  const normalizeString = (str) => {
    return removeSpecialCharacter(
      str.trim().replace(/\s+/g, " ")
    ).toUpperCase();
  };

  const validateDataImport = (objColumns) => {
    let errMsg = "";
    let invalidColumns = [];

    if (!objColumns) {
      errMsg = "Data not found. Please update the file with the required data and try again.\n";
    }
    else if (fileTypeDetails?.fileColumns?.filter((x) => x.active)?.length <= 0) {
      errMsg = "Selected file type does not have active column(s). Please update the file type with the active column(s) and try again.\n";
    } else {
      const columNames = fileTypeDetails?.fileColumns
        ?.filter((x) => x.active)
        .map((obj) => obj["name"]);

      if (columNames && objColumns) {
        const normalizedObjColumns = objColumns.map((col) =>
          normalizeString(col)
        );

        columNames.forEach((t) => {
          const normalizedColumn = normalizeString(t);

          if (normalizedObjColumns.indexOf(normalizedColumn) < 0) {
            invalidColumns.push(t);
          }
        });
      }

      if (invalidColumns?.length > 0) {
        errMsg = "Column name is missing. Please update the file with the required columns and try again.\n";
      }
      else {
        const duplicatesNames = objColumns?.filter((item, index) => objColumns?.indexOf(item) !== index);
        if (duplicatesNames?.length > 0) {
          invalidColumns = duplicatesNames;
          errMsg = "Duplicate column name discovered. Please remove any duplicate columns and try again.\n";
        }
      }
    }

    if (errMsg !== "") {
      messageDispatch(setMessageHandler({ type: "validationError", message: errMsg, list: invalidColumns }));
      return false;
    }
    updateDataImportSelectedValues("isValidFile", true);
  };

  const importButtonClick = () => {
    const formData = new FormData();
    formData.append("", fileObj);
    formData.append("facilityKey", facilityId);
    formData.append("fileTypeKey", dataImportSelectedValues.fileTypeKey);
    formData.append("datasourceKey", dataImportSelectedValues.dataSourceKey);
    formData.append("rowToStartsWith", dataImportSelectedValues.rowToStartWith);
 
    updateDataImportSelectedValues("isValidFile", false);
    const getSendFileToImportResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        messageDispatch(
          setMessageHandler({
            type: "validationError",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : obj["errors"]?.length > 0
                ? "\n" +
                  "Backend process found below issues." +
                  "\n" +
                  obj["errors"]?.map((x) => x.errorMessage).join("\n")
                : ""),
          })
        );
        return;
      }
      messageDispatch(
        setMessageHandler({
          type: "success",
          message: "Data imported successfully!",
        })
      );
      setFileObj({});
      setIsVisible(false);
    };

    try {
      sendFileToImport(
        { url: "DataSources/Import", method: "fileUpload", body: formData },
        getSendFileToImportResponse
      );
     }
     catch (error) {
      console.log("Error while importing data: " + error.message);
    }
  };

  const dataSourceChangeHandler = (field, id, value) => {
    if (value) {
      messageDispatch(setMessageHandler({ type: null, message: null }));
      setIsVisible(false);
      setFileObj({});
      updateDataImportSelectedValues({
        dataSourceKey: value,
        fileTypeKey: 0,
        rowToStartWith: 0,
        isValidFile: false
      });

      const selectedDataSource = dataImportSource?.filter(
        (item) => item.datasourceKey === value
      );
      if (selectedDataSource?.length > 0) {
        setFileTypes(
          selectedDataSource[0]?.["fileTypes"]?.map((t) => {
            return {
              id: t["fileTypeKey"],
              label: t["name"],
              rowToStartWith: t["rowToStartWith"],
            };
          })
        );
      } else {
        setFileTypes([]);
      }
    } else {
      setFileTypes([]);
    }
  };

  const fileTypeChangeHandler = (field, id, value) => {
    if (value) {
      const tmpSelectedFileType = fileTypes?.filter((t) => t["id"] === value);
      if (tmpSelectedFileType?.length > 0) {
        updateDataImportSelectedValues({
          fileTypeKey: value,
          rowToStartWith: tmpSelectedFileType[0]?.["rowToStartWith"],
        });
      }

      const getFileTypeDetailsResponse = (obj) => {
        if (!obj || !obj.isSuccess || obj.errorResponse) {
          console.log("File type columns fetch response returns error");
          return;
        }
        setFileTypeDetails(obj["fileType"]);
      };

      fetchFileTypeDetails(
        {
          url: "FileTypes/" + value,
          method: "GET",
          header: true,
        },
        getFileTypeDetailsResponse
      );
    } else {
      setFileTypeDetails([]);
    }
  };

  const updateDataImportSelectedValues = (key, value) => {
    if (typeof key === "object") {
      setDataImportSelectedValues({ ...dataImportSelectedValues, ...key });
    } else {
      setDataImportSelectedValues({
        ...dataImportSelectedValues,
        [key]: value,
      });
    }
  };

  return (
    <div
      style={{ display: "flex", gap: "20px", height: "calc(100vh - 160px)" }}
    >
      <Backdrop
        className="backDrop"
        open={isDataSourceLoaded ? true : isImportProcessed ? true : false}
      >
        <CircularProgress color="inherit" />
        <p>Please wait...</p>
      </Backdrop>
      <Backdrop
        className="backDrop"
        open={isUploading}
      >
        <CircularProgress color="inherit" />
        <p>Uploading file, please wait...</p>
      </Backdrop>
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "20px",
          width: "100%",
        }}
      >
        <div style={{ width: "100%" }}>
          <Grid container sx={{ display: "flex" }}>
            <Grid item xs={4}>
              <div style={{ display: "flex" }}>
                <b>Data Source:</b> &nbsp;
                <div style={{ width: "75%" }}>
                  <MuiComboBox
                    name={"dataImport-dataSource"}
                    id={"dataImport-dataSource"}
                    list={dataSourceTypes}
                    isError={false}
                    onChange={dataSourceChangeHandler}
                  />
                </div>
              </div>
            </Grid>
            <Grid item xs={4}>
              <div style={{ display: "flex" }}>
                <b>File Type:</b>&nbsp;
                <div style={{ width: "75%" }}>
                  <MuiComboBox
                    name={"dataImport-fileType"}
                    id={"dataImport-fileType"}
                    list={fileTypes}
                    isError={false}
                    onChange={fileTypeChangeHandler}
                  />
                </div>
              </div>
            </Grid>
            <Grid item xs={4} sx={{ display: "flex" }}>
              <div>
                <label className="custom-file-upload" style={isDisabled ? disabledButtonStyle : buttonStyle}>
                  <input
                    ref={fileInputRef}
                    key={fileObj?.name || "file-input"}
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    disabled={isDisabled}
                  />
                  Browse
                </label>
                &nbsp;&nbsp;
                {fileObj?.name}
              </div>
            </Grid>
          </Grid>
        </div>

        <MessageDisplay />

        {dataImportSelectedValues?.isValidFile && (
          <Stack
            direction="row"
            textAlign={"center"}
            display={"block"}
            spacing={2}
          >
            <Button
              size="md"
              variant="contained"
              onClick={() => {
                resetValues();
              }}              
            >
              Clear
            </Button>
            <Button
              size="md"
              variant="contained"
              onClick={() => {
                importButtonClick();
              }}             
            >
              Import
            </Button>
          </Stack>
        )}
        {isVisible &&(
        <Stack direction="row" textAlign={"center"} display={"block"} spacing={2}>
          <div style={{color: 'blue'}}>
          <p>Please ensure that your .csv file is system compliant by carrying out following steps:-</p>
          <p>Step 1) Open your .csv file on MS-Excel </p>
          <p>Step 2) Press Ctrl+F to open Find and Replace pop-up.</p>
          <p>Step 3) Press Ctrl+J</p>
          <p>Step 4) Switch to Replace tab on Find and replace pop-up</p>
          <p>Step 5) Press Replace All and save file.</p>
          <p>Your file is ready to be imported on system.</p>
          </div>
        </Stack>
        )}
      </div>
    </div>
  );
};
